// COINFLIP.EXE — the one cabinet whose coins are genuinely fair.
//
// One to four coins, all of them your side, pays exactly 2^N. The 5% fee is the whole edge, and
// this is the only place in the casino that sentence is allowed to appear.
//
// Gold coins on felt. They spin when the round lands, and what they show is the contract's
// facesOf for that round — never a coin this component tossed itself.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { CHIP, revealCoinFlip } from 'lib/casino';
import sfx from 'lib/sfx';
import { AutoPanel, useAuto } from './auto';
import { useBet, useStakeCap } from './state';
import { FONT, GroupBox, HouseRules, Outcome, Panel, PlayButton, Radio, StakeBox, T, fmtChip, parseUnits, xpButton } from './ui';

export default function CoinFlip({ casino }) {
  const [amount, setAmount] = useState('5');
  const [coins, setCoins] = useState(1);
  const [side, setSide] = useState(1);
  const [faces, setFaces] = useState(null);
  const bet = useBet('coinFlip', casino);

  const locked = bet.phase !== 'idle' && bet.phase !== 'done';

  useEffect(() => {
    let alive = true;
    setFaces(null);
    if (!bet.result) return undefined;
    revealCoinFlip(bet.result.id, coins, bet.result.randomness)
      .then(f => {
        if (alive) setFaces(Number(f));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bet.result]);

  const allIn = useMemo(() => parseUnits(amount, CHIP.decimals), [amount]);
  const stake = (allIn * BigInt(10000)) / BigInt(10500);
  const multBps = 10000 * Math.pow(2, coins); // plain Number maths — never BigInt `**`
  const payout = (stake * BigInt(multBps)) / BigInt(10000);
  const chance = 100 / Math.pow(2, coins);

  // The house refuses a bet whose worst case it cannot cover, and the refusal is a revert with
  // no explanation. Read the live ceiling and never let the player submit past it.
  const stakeCap = useStakeCap('coinFlip', multBps, casino, amount, setAmount);
  const overCap = stakeCap !== null && stakeCap > BigInt(0) && allIn > stakeCap;
  const canPlay = casino.ready && allIn > BigInt(0) && !overCap;
  const auto = useAuto(bet, { allIn, args: [coins, side], canPlay, balance: casino.balance });
  const spinning =bet.phase === 'waiting' || bet.phase === 'settling' || bet.phase === 'placing' || bet.phase === 'approving';

  // The coins only start turning once, on the edge into a pending round.
  useEffect(() => {
    if (spinning) sfx.wheelSpin(700, 6);
  }, [spinning]);

  return (
    <Div>
      <div className="cf__table">
        <div className="cf__rail" />
        {Array.from({ length: coins }).map((_, i) => {
          const landed = faces === null ? null : (faces >> i) & 1;
          const matched = landed !== null && landed === side;
          const cls = ['cf__coin', landed === null ? '' : matched ? 'good' : 'bad', spinning ? 'spin' : '', landed !== null ? 'land' : '']
            .filter(Boolean)
            .join(' ');
          return (
            <div key={i} className={cls}>
              <span>{landed === null ? (side === 1 ? 'H' : 'T') : landed === 1 ? 'H' : 'T'}</span>
            </div>
          );
        })}
      </div>
      <div className="cf__caption">
        {faces === null
          ? `all ${coins} must land ${side === 1 ? 'heads' : 'tails'}`
          : `the coins landed — ${bet.result && bet.result.won ? 'every one yours' : 'not every one yours'}`}
      </div>

      <Panel>
        <div className="cf__controls">
          <GroupBox legend="COINS" className="cf__group">
            {[1, 2, 3, 4].map(c => (
              <Radio key={c} name="cf-coins" value={c} checked={c === coins} disabled={locked} onChange={setCoins} label={String(c)} />
            ))}
          </GroupBox>
          <GroupBox legend="SIDE" className="cf__group">
            <Radio name="cf-side" value={1} checked={side === 1} disabled={locked} onChange={setSide} label="HEADS" />
            <Radio name="cf-side" value={0} checked={side === 0} disabled={locked} onChange={setSide} label="TAILS" />
          </GroupBox>
        </div>

        <div className="cf__quote">
          <div>
            <span className="k">Pays</span>
            <span className="v">{(multBps / 10000).toFixed(0)}x</span>
          </div>
          <div>
            <span className="k">Chance</span>
            <span className="v">{chance.toFixed(2)}%</span>
          </div>
          <div>
            <span className="k">Returns</span>
            <span className="v">
              {fmtChip(payout)} {CHIP.symbol}
            </span>
          </div>
        </div>

        <StakeBox value={amount} onChange={setAmount} disabled={locked} />

        <PlayButton
          label="FLIP"
          onClick={() => bet.play(allIn, [coins, side])}
          disabled={!canPlay}
          phase={bet.phase}
          note={bet.note}
          countdown={bet.countdown}
        />

        <AutoPanel auto={auto} practice={casino.practice} />

        {bet.phase === 'stuck' ? (
          <div className="cf__warn">
            Your bet is placed and still open. <button onClick={bet.retrySettle}>Settle again</button>
          </div>
        ) : null}

        <Outcome result={bet.result} onAgain={bet.reset} />

        <HouseRules
          fair
          practice={casino.practice}
        />
      </Panel>
    </Div>
  );
}

const Div = styled.div`
  .cf__table {
    position: relative;
    background: radial-gradient(ellipse at 50% 30%, #1a8a4c, ${T.felt} 55%, ${T.feltDark} 100%);
    border: 4px solid #5a3a12;
    border-radius: 60px / 26px;
    box-shadow: inset 0 0 0 2px ${T.goldDeep}, inset 0 0 40px rgba(0, 0, 0, 0.55), 0 4px 8px rgba(0, 0, 0, 0.5);
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 18px 16px;
    perspective: 600px;
  }
  .cf__rail {
    position: absolute;
    inset: 10px;
    border: 1px dashed rgba(255, 216, 107, 0.35);
    border-radius: 50px / 20px;
    pointer-events: none;
  }
  .cf__coin {
    width: 58px;
    height: 58px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #fff3c4, #ffd86b 38%, #c9a227 70%, #7a5c00 100%);
    box-shadow: inset 0 0 0 3px rgba(122, 92, 0, 0.6), inset 0 0 0 5px rgba(255, 243, 196, 0.5), 0 3px 4px rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    transform-style: preserve-3d;
  }
  .cf__coin span {
    font: bold 24px ${FONT.serif};
    color: #5a4200;
    text-shadow: 0 1px 0 #fff2c0, 0 -1px 0 #7a5c00;
  }
  .cf__coin.spin {
    animation: cf-spin 0.7s linear infinite;
  }
  .cf__coin.land {
    animation: cf-land 0.9s cubic-bezier(0.16, 1, 0.3, 1) 1;
  }
  .cf__coin.good {
    box-shadow: inset 0 0 0 3px rgba(122, 92, 0, 0.6), inset 0 0 0 5px rgba(255, 243, 196, 0.5), 0 0 18px rgba(255, 216, 107, 0.9);
  }
  .cf__coin.bad {
    filter: grayscale(0.9) brightness(0.75);
  }
  @keyframes cf-spin {
    from {
      transform: rotateY(0deg);
    }
    to {
      transform: rotateY(360deg);
    }
  }
  @keyframes cf-land {
    from {
      transform: rotateY(540deg) scale(0.8);
    }
    to {
      transform: rotateY(0deg) scale(1);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .cf__coin.spin,
    .cf__coin.land {
      animation: none;
    }
  }
  .cf__caption {
    text-align: center;
    font: 10px ${FONT.web};
    color: ${T.cream};
    margin-top: 7px;
  }
  .cf__controls {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
  .cf__group {
    flex: 1;
    min-width: 150px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }
  .cf__quote {
    display: flex;
    gap: 6px;
    margin-top: 10px;
    flex-wrap: wrap;
  }
  .cf__quote > div {
    flex: 1;
    min-width: 92px;
    background: #fff;
    border: 1px solid #7f9db9;
    box-shadow: inset 1px 1px 0 #d6dde5;
    padding: 3px 6px;
  }
  .cf__quote .k {
    display: block;
    font-size: 9px;
    color: #555;
  }
  .cf__quote .v {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #000;
    margin-top: 1px;
  }
  .cf__warn {
    margin-top: 8px;
    font: 10px ${FONT.ui};
    color: #8a0000;
  }
  .cf__warn button {
    ${xpButton};
    min-width: 0;
    min-height: 19px;
    padding: 0 8px;
    margin-left: 6px;
  }
`;
